import React, { useState, useContext, useEffect, useMemo, useRef } from "react";
import { useTranslation } from 'react-i18next';
import { useNavigate } from "react-router-dom";
import './style.css';
import '../../styles/modern-settings.css';
import { SidebarContext } from "../../AppRoutes";
import PermissionGate from "../../services/PermissionGate";
import { FaClock, FaSave, FaPlus, FaTrash, FaGlobe, FaCheck, FaSearch, FaChevronDown } from "react-icons/fa";
import { SlActionUndo } from "react-icons/sl";
import { getStoredUser } from '../../utils/safeStorage';
import { updateTempoHorarioFuncionamento, getEmpresaWithObjId } from "../../services/api";
import { updateEmpresaLocalStorage } from "../../utils/timezone";
import { toast } from "react-toastify";

const HorarioFuncionamento = ({ embedded = false, onSave, onBack }) => {
    const { t } = useTranslation('storeInfo');

    const userParse = getStoredUser();
    const userID = userParse?._id;
    const empresa = localStorage.getItem('empresa');
    const empresaParse = JSON.parse(empresa);
    const idEmpresa = empresaParse.id_empresa;
    const empresaObjId = empresaParse._id;

    const { sidebar } = useContext(SidebarContext);
    const navigate = useNavigate();

    const [availability, setAvailability] = useState('especifico');
    const [activeDays, setActiveDays] = useState(new Set());
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedTimeZone, setSelectedTimeZone] = useState('America/Sao_Paulo');
    const [timezoneSearch, setTimezoneSearch] = useState('');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);

    const initialTimeRange = { start: '08:00', end: '18:00' };
    const initialTimeRanges = {
        domingo: [initialTimeRange],
        segunda: [initialTimeRange],
        terca: [initialTimeRange],
        quarta: [initialTimeRange],
        quinta: [initialTimeRange],
        sexta: [initialTimeRange],
        sabado: [initialTimeRange],
    };
    const [timeRanges, setTimeRanges] = useState(initialTimeRanges);

    // Obter todos os fusos horarios do browser (internacionalizacao)
    const allTimezones = useMemo(() => {
        try {
            return Intl.supportedValuesOf('timeZone');
        } catch {
            // Fallback para browsers antigos
            return [
                'America/Sao_Paulo', 'America/Fortaleza', 'America/Manaus', 'America/Rio_Branco',
                'America/New_York', 'America/Los_Angeles', 'America/Chicago',
                'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Madrid',
                'Asia/Tokyo', 'Asia/Shanghai', 'Asia/Dubai', 'Asia/Singapore',
                'Australia/Sydney', 'Pacific/Auckland'
            ];
        }
    }, []);

    // Agrupar fusos por regiao
    const groupedTimezones = useMemo(() => {
        const groups = {};
        allTimezones.forEach(tz => {
            const region = tz.split('/')[0];
            if (!groups[region]) groups[region] = [];
            groups[region].push(tz);
        });
        // Ordenar regioes e fusos dentro de cada regiao
        const sortedGroups = {};
        Object.keys(groups).sort().forEach(region => {
            sortedGroups[region] = groups[region].sort();
        });
        return sortedGroups;
    }, [allTimezones]);

    // Funcao para remover acentos e normalizar texto para busca
    const normalizeText = (text) => {
        return text
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '') // Remove acentos
            .replace(/_/g, ' ')              // Substitui underscore por espaco
            .replace(/\//g, ' ');            // Substitui barra por espaco
    };

    // Filtrar fusos baseado na busca (ignora acentos e busca global)
    const filteredTimezones = useMemo(() => {
        if (!timezoneSearch.trim()) return groupedTimezones;

        const search = normalizeText(timezoneSearch);
        const filtered = {};

        Object.entries(groupedTimezones).forEach(([region, zones]) => {
            const matchingZones = zones.filter(tz => {
                const normalizedTz = normalizeText(tz);
                const normalizedRegion = normalizeText(region);
                // Busca em qualquer parte do nome do fuso ou regiao
                return normalizedTz.includes(search) || normalizedRegion.includes(search);
            });
            if (matchingZones.length > 0) {
                filtered[region] = matchingZones;
            }
        });

        return filtered;
    }, [groupedTimezones, timezoneSearch]);

    const daysOfWeek = ['domingo', 'segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado'];

    const dayLabels = {
        domingo: { short: t('businessHours.days.sunday', 'Dom').charAt(0), full: t('businessHours.days.sunday', 'Domingo') },
        segunda: { short: t('businessHours.days.monday', 'Seg').charAt(0), full: t('businessHours.days.monday', 'Segunda-feira') },
        terca: { short: t('businessHours.days.tuesday', 'Ter').charAt(0), full: t('businessHours.days.tuesday', 'Terca-feira') },
        quarta: { short: t('businessHours.days.wednesday', 'Qua').charAt(0), full: t('businessHours.days.wednesday', 'Quarta-feira') },
        quinta: { short: t('businessHours.days.thursday', 'Qui').charAt(0), full: t('businessHours.days.thursday', 'Quinta-feira') },
        sexta: { short: t('businessHours.days.friday', 'Sex').charAt(0), full: t('businessHours.days.friday', 'Sexta-feira') },
        sabado: { short: t('businessHours.days.saturday', 'Sab').charAt(0), full: t('businessHours.days.saturday', 'Sabado') },
    };

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const empresaTemp = await getEmpresaWithObjId(empresaObjId);

                if (empresaTemp?.data?.empresa?.horario_funcionamento) {
                    setTimeRanges(prev => ({
                        ...prev,
                        ...empresaTemp.data.empresa.horario_funcionamento
                    }));
                    const daysActive = new Set(Object.keys(empresaTemp.data.empresa.horario_funcionamento));
                    setActiveDays(daysActive);
                }

                if (empresaTemp?.data?.empresa?.status_loja) {
                    setAvailability(empresaTemp.data.empresa.status_loja);
                }

                if (empresaTemp?.data?.empresa?.timezone) {
                    setSelectedTimeZone(empresaTemp.data.empresa.timezone);
                }
            } catch (error) {
                console.error("Erro ao buscar dados da empresa:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [empresaObjId]);

    // Fechar dropdown ao clicar fora
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Selecionar timezone e fechar dropdown
    const handleSelectTimezone = (tz) => {
        setSelectedTimeZone(tz);
        setIsDropdownOpen(false);
        setTimezoneSearch('');
    };

    const handleBack = () => {
        if (onBack) {
            onBack();
        } else {
            navigate("/");
        }
    };

    const handleAvailabilityChange = (value) => {
        setAvailability(value);
    };

    const toggleDay = (day) => {
        setActiveDays((prevActiveDays) => {
            const newActiveDays = new Set(prevActiveDays);
            if (newActiveDays.has(day)) {
                newActiveDays.delete(day);
            } else {
                newActiveDays.add(day);
                if (!timeRanges[day] || timeRanges[day].length === 0) {
                    addTimeRange(day);
                }
            }
            return newActiveDays;
        });
    };

    const handleTimeChange = (day, index, field, value) => {
        setTimeRanges((prevTimeRanges) => {
            const updatedRanges = [...(prevTimeRanges[day] || [])];
            updatedRanges[index] = { ...updatedRanges[index], [field]: value };
            return {
                ...prevTimeRanges,
                [day]: updatedRanges,
            };
        });
    };

    const addTimeRange = (day) => {
        setTimeRanges((prevTimeRanges) => {
            const newRanges = prevTimeRanges[day] ? [...prevTimeRanges[day]] : [];
            newRanges.push({ start: '08:00', end: '18:00' });
            return {
                ...prevTimeRanges,
                [day]: newRanges,
            };
        });
    };

    const removeTimeRange = (day, index) => {
        setTimeRanges((prevTimeRanges) => {
            const updatedRanges = [...prevTimeRanges[day]];
            updatedRanges.splice(index, 1);
            return {
                ...prevTimeRanges,
                [day]: updatedRanges,
            };
        });
    };

    const saveSchedule = async () => {
        setIsSubmitting(true);

        const activeTimeRanges = {};
        daysOfWeek.forEach((day) => {
            if (activeDays.has(day)) {
                const dayRanges = timeRanges[day];
                if (dayRanges && dayRanges.length > 0) {
                    activeTimeRanges[day] = dayRanges;
                }
            }
        });

        try {
            const status_loja = availability;
            const horario_funcionamento = availability === 'sempre' ? {} : activeTimeRanges;
            const timezone = selectedTimeZone;

            const response = await updateTempoHorarioFuncionamento(
                userID, empresaObjId, idEmpresa, status_loja, horario_funcionamento, timezone
            );

            if (response) {
                // Atualizar localStorage para refletir mudanças sem precisar re-login
                updateEmpresaLocalStorage({
                    timezone,
                    status_loja,
                    horario_funcionamento,
                });
                toast.success(response.data.msg || t('toasts.hoursSaved', 'Horarios salvos com sucesso!'));
                if (onSave) onSave();
            }
        } catch (error) {
            toast.error(t('toasts.hoursUpdateError', 'Erro ao salvar horarios'));
        } finally {
            setIsSubmitting(false);
        }
    };

    // Calcular offset UTC de um fuso horario no formato padronizado (UTC-03:00)
    const getUtcOffset = (timezone) => {
        try {
            const now = new Date();

            // Criar formatadores para UTC e timezone alvo
            const utcDate = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' }));
            const tzDate = new Date(now.toLocaleString('en-US', { timeZone: timezone }));

            // Calcular diferenca em minutos
            const diffMinutes = (tzDate - utcDate) / (1000 * 60);

            // Converter para horas e minutos
            const hours = Math.floor(Math.abs(diffMinutes) / 60);
            const minutes = Math.abs(diffMinutes) % 60;

            // Determinar sinal
            const sign = diffMinutes >= 0 ? '+' : '-';

            // Formatar com dois digitos: UTC+03:00 ou UTC-05:30
            const hoursStr = hours.toString().padStart(2, '0');
            const minutesStr = minutes.toString().padStart(2, '0');

            return `UTC${sign}${hoursStr}:${minutesStr}`;
        } catch {
            return '';
        }
    };

    // Cache dos offsets para performance
    const utcOffsets = useMemo(() => {
        const offsets = {};
        allTimezones.forEach(tz => {
            offsets[tz] = getUtcOffset(tz);
        });
        return offsets;
    }, [allTimezones]);

    // Formatar nome do fuso para exibicao
    const formatTimezoneName = (tz) => {
        const name = tz.replace(/_/g, ' ').replace(/\//g, ' / ');
        const utc = utcOffsets[tz] || getUtcOffset(tz);
        return utc ? `${name} (${utc})` : name;
    };

    // Conteudo do formulario
    const formContent = (
        <div className="horario-modern-wrapper">
            {/* Card de Fuso Horario */}
            <div className="horario-modern-card">
                <div className="horario-modern-card-header">
                    <div className="horario-modern-card-icon">
                        <FaGlobe />
                    </div>
                    <div>
                        <h3 className="horario-modern-card-title">{t('businessHours.timezone.label', 'Fuso Horario')}</h3>
                        <p className="horario-modern-card-subtitle">{t('businessHours.timezone.description', 'Selecione o fuso horario do seu estabelecimento')}</p>
                    </div>
                </div>

                <div className="horario-timezone-container" ref={dropdownRef}>
                    {/* Custom Dropdown */}
                    <div className="horario-timezone-dropdown">
                        <button
                            type="button"
                            className={`horario-timezone-trigger ${isDropdownOpen ? 'open' : ''}`}
                            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                        >
                            <FaGlobe className="horario-timezone-trigger-icon" />
                            <span>{formatTimezoneName(selectedTimeZone)}</span>
                            <FaChevronDown className={`horario-timezone-arrow ${isDropdownOpen ? 'open' : ''}`} />
                        </button>

                        {isDropdownOpen && (
                            <div className="horario-timezone-dropdown-content">
                                {/* Search Input */}
                                <div className="horario-timezone-search-wrapper">
                                    <FaSearch className="horario-timezone-search-icon" />
                                    <input
                                        type="text"
                                        className="horario-timezone-search"
                                        placeholder={t('businessHours.timezone.searchPlaceholder', 'Buscar fuso horario...')}
                                        value={timezoneSearch}
                                        onChange={(e) => setTimezoneSearch(e.target.value)}
                                        autoFocus
                                    />
                                </div>

                                {/* Scrollable List */}
                                <div className="horario-timezone-list">
                                    {Object.entries(filteredTimezones).map(([region, zones]) => (
                                        <div key={region} className="horario-timezone-group">
                                            <div className="horario-timezone-group-label">{region}</div>
                                            {zones.map((zone) => (
                                                <button
                                                    key={zone}
                                                    type="button"
                                                    className={`horario-timezone-option ${selectedTimeZone === zone ? 'selected' : ''}`}
                                                    onClick={() => handleSelectTimezone(zone)}
                                                >
                                                    {selectedTimeZone === zone && <FaCheck className="horario-timezone-check" />}
                                                    {formatTimezoneName(zone)}
                                                </button>
                                            ))}
                                        </div>
                                    ))}

                                    {Object.keys(filteredTimezones).length === 0 && (
                                        <div className="horario-timezone-empty">
                                            {t('businessHours.timezone.noResults', 'Nenhum fuso horario encontrado')}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Card de Disponibilidade */}
            <div className="horario-modern-card">
                <div className="horario-modern-card-header">
                    <div className="horario-modern-card-icon">
                        <FaClock />
                    </div>
                    <div>
                        <h3 className="horario-modern-card-title">{t('businessHours.availability.title', 'Disponibilidade')}</h3>
                        <p className="horario-modern-card-subtitle">{t('businessHours.availability.description', 'Defina quando seu estabelecimento esta disponivel')}</p>
                    </div>
                </div>

                <div className="horario-availability-options">
                    <label
                        className={`horario-availability-option ${availability === 'sempre' ? 'active' : ''}`}
                        onClick={() => handleAvailabilityChange('sempre')}
                    >
                        <div className="horario-radio-circle">
                            {availability === 'sempre' && <div className="horario-radio-dot" />}
                        </div>
                        <div className="horario-option-content">
                            <span className="horario-option-title">{t('businessHours.availability.open24h', 'Sempre disponivel')}</span>
                            <span className="horario-option-desc">{t('businessHours.availability.open24hDesc', 'Seu estabelecimento estara aberto 24 horas, todos os dias')}</span>
                        </div>
                    </label>

                    <label
                        className={`horario-availability-option ${availability === 'especifico' ? 'active' : ''}`}
                        onClick={() => handleAvailabilityChange('especifico')}
                    >
                        <div className="horario-radio-circle">
                            {availability === 'especifico' && <div className="horario-radio-dot" />}
                        </div>
                        <div className="horario-option-content">
                            <span className="horario-option-title">{t('businessHours.availability.specificHours', 'Horarios especificos')}</span>
                            <span className="horario-option-desc">{t('businessHours.availability.specificHoursDesc', 'Defina dias e horarios de funcionamento')}</span>
                        </div>
                    </label>
                </div>
            </div>

            {/* Card de Dias e Horarios */}
            {availability === 'especifico' && (
                <div className="horario-modern-card">
                    <div className="horario-modern-card-header">
                        <div className="horario-modern-card-icon">
                            <FaClock />
                        </div>
                        <div>
                            <h3 className="horario-modern-card-title">{t('businessHours.daysOfWeek', 'Dias da Semana')}</h3>
                            <p className="horario-modern-card-subtitle">{t('businessHours.daysDescription', 'Selecione os dias e configure os horarios')}</p>
                        </div>
                    </div>

                    {/* Seletor de Dias */}
                    <div className="horario-days-selector">
                        {daysOfWeek.map((day) => (
                            <button
                                key={day}
                                type="button"
                                className={`horario-day-button ${activeDays.has(day) ? 'active' : ''}`}
                                onClick={() => toggleDay(day)}
                                title={dayLabels[day].full}
                            >
                                {activeDays.has(day) && <FaCheck className="horario-day-check" />}
                                <span>{dayLabels[day].short}</span>
                            </button>
                        ))}
                    </div>

                    {/* Horarios por Dia */}
                    <div className="horario-schedules-container">
                        {daysOfWeek.map((day) => (
                            activeDays.has(day) && (
                                <div key={day} className="horario-day-schedule">
                                    <div className="horario-day-header">
                                        <h4>{dayLabels[day].full}</h4>
                                    </div>

                                    <div className="horario-time-ranges">
                                        {Array.isArray(timeRanges[day]) && timeRanges[day].map((range, index) => (
                                            <div key={index} className="horario-time-range">
                                                <div className="horario-time-inputs">
                                                    <div className="horario-time-input-group">
                                                        <label>{t('businessHours.timeLabels.from', 'Das')}</label>
                                                        <input
                                                            type="time"
                                                            value={range.start || '08:00'}
                                                            onChange={(e) => handleTimeChange(day, index, 'start', e.target.value)}
                                                        />
                                                    </div>

                                                    <span className="horario-time-separator">{t('businessHours.timeLabels.to', 'ate')}</span>

                                                    <div className="horario-time-input-group">
                                                        <label>{t('businessHours.timeLabels.until', 'Ate')}</label>
                                                        <input
                                                            type="time"
                                                            value={range.end || '18:00'}
                                                            onChange={(e) => handleTimeChange(day, index, 'end', e.target.value)}
                                                        />
                                                    </div>

                                                    {index > 0 && (
                                                        <button
                                                            type="button"
                                                            className="horario-remove-btn"
                                                            onClick={() => removeTimeRange(day, index)}
                                                            title={t('businessHours.removeTime', 'Remover horario')}
                                                        >
                                                            <FaTrash />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        ))}

                                        <button
                                            type="button"
                                            className="horario-add-time-btn"
                                            onClick={() => addTimeRange(day)}
                                        >
                                            <FaPlus />
                                            <span>{t('businessHours.addTime', 'Adicionar horario')}</span>
                                        </button>
                                    </div>
                                </div>
                            )
                        ))}

                        {Array.from(activeDays).length === 0 && (
                            <div className="horario-empty-state">
                                <FaClock className="horario-empty-icon" />
                                <p>{t('businessHours.selectDaysMessage', 'Selecione os dias da semana acima para configurar os horarios')}</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );

    // Loading state
    if (isLoading) {
        return (
            <div className="modern-loading">
                <div className="modern-spinner"></div>
                <p>{t('loading', 'Carregando...')}</p>
            </div>
        );
    }

    // Se estiver embedded, retorna conteudo com header estilizado (igual Dados da Loja)
    if (embedded) {
        return (
            <div className="horario-embedded-container">
                {/* Header com icone e titulo - Estilo igual Dados da Loja */}
                <div className="horario-section-header">
                    <div className="horario-header-content">
                        <div className="horario-header-icon">
                            <FaClock />
                        </div>
                        <div>
                            <h2>{t('businessHours.title', 'Horario de Funcionamento')}</h2>
                            <p>{t('businessHours.pageSubtitle', 'Configure os horarios de atendimento do seu estabelecimento')}</p>
                        </div>
                    </div>
                    <button
                        type="button"
                        className="modern-btn modern-btn-save"
                        onClick={saveSchedule}
                        disabled={isSubmitting}
                    >
                        <FaSave />
                        {isSubmitting ? t('businessHours.saving', 'Salvando...') : t('businessHours.saveButton', 'Salvar Horarios')}
                    </button>
                </div>
                {formContent}
            </div>
        );
    }

    // Modo normal com wrapper completo
    return (
        <PermissionGate permissions={['default']}>
            <div
                className="modern-settings-container"
                style={{
                    marginLeft: sidebar ? '250px' : '100px',
                    width: sidebar ? 'calc(100% - 250px)' : 'calc(100% - 100px)'
                }}
            >
                <div className="modern-settings-header-with-actions">
                    <div className="modern-settings-title-section">
                        <h1>{t('businessHours.title', 'Horario de Funcionamento')}</h1>
                        <p className="subtitle">{t('businessHours.pageSubtitle', 'Configure os horarios de atendimento do seu estabelecimento')}</p>
                    </div>
                    <div className="modern-settings-actions">
                        <button
                            type="button"
                            className="modern-btn modern-btn-save"
                            onClick={saveSchedule}
                            disabled={isSubmitting}
                        >
                            <FaSave />
                            {isSubmitting ? t('businessHours.saving', 'Salvando...') : t('businessHours.saveButton', 'Salvar Horarios')}
                        </button>
                        <button
                            type="button"
                            className="modern-btn modern-btn-secondary"
                            onClick={handleBack}
                        >
                            <SlActionUndo />
                            {t('common:buttons.back')}
                        </button>
                    </div>
                </div>

                <div className="modern-settings-content">
                    {formContent}
                </div>
            </div>
        </PermissionGate>
    );
};

export default HorarioFuncionamento;
